"use client";

import { create } from "zustand";
import type {
  User,
  Team,
  Label,
  Issue,
  Project,
  Cycle,
  Workspace,
  Doc,
  Note,
  ProgressCard,
  ProgressMetric,
  StatusId,
  PriorityId,
  Comment,
  Feedback,
  FeedbackSeverity,
  WorkspaceLink,
  AgentRun,
  AgentSession,
  WorkspaceMember,
} from "./types";
import { uid } from "./utils";
import { createClient } from "./supabase/client";
import { parseTaskReportOrigin, type TaskReportChannel } from "./taskReportOrigin";
import {
  mapWorkspace,
  mapProfile,
  mapTeam,
  mapLabel,
  mapProject,
  mapCycle,
  mapIssue,
  mapDoc,
  mapNote,
  mapProgressCard,
  mapFeedback,
  mapWorkspaceLink,
  mapAgentRun,
  mapAgentSession,
  PRIORITY_TO_DB,
} from "./storeMappers";
import type { Database } from "@/types/database";
import {
  groupTeamMemberIds,
  preferredTeamForUser,
  replaceTeamMemberIdsForWorkspace,
} from "./teamMemberships";
import {
  persistOnboardingCompletion,
  readOnboardingMetadata,
} from "./onboardingPersistence";
import { loadAllLiveDocs } from "./docsInventory";
import { beginTaskWrite, waitForTaskWrites } from "./taskWriteLifecycle";
import {
  loadWorkspaceEntityTarget,
  type WorkspaceEntityTargetInput,
  type WorkspaceEntityTargetResult,
} from "./data/workspaceEntityTarget";

let workspaceEntityTargetRequest = 0;
const workspaceEntitySnapshotRevisions = new Map<string, number>();
const taskEditVersions = new Map<string, symbol>();

function advanceWorkspaceEntitySnapshot(workspaceId: string) {
  const key = workspaceId.toLowerCase();
  workspaceEntitySnapshotRevisions.set(key, (workspaceEntitySnapshotRevisions.get(key) ?? 0) + 1);
}

const AGENT_RUN_COLUMNS =
  "id,session_id,workspace_id,actor_id,prompt,summary,actions,touched_issue_ids,touched_doc_ids,project_ids,started_at,ended_at,status,duration_ms,created_at,updated_at,heartbeat_at,current_step,external_run_key,parent_run_id,agent_label,source,replaces_run_id" as const;

function mapUniqueAgentRuns(
  rows: Database["public"]["Tables"]["agent_runs"]["Row"][]
) {
  const byId = new Map(
    rows.map((row) => [row.id, mapAgentRun(row)])
  );
  return [...byId.values()].sort((a, b) => b.startedAt - a.startedAt);
}

interface State {
  hydrated: boolean;
  loading: boolean;
  error: string | null;
  /** Bootstrap failure only; later mutation errors must not block route targets. */
  hydrationError: string | null;
  currentUserId: string;
  onboardingVersion: number;
  onboardingCompletedAt: number | null;
  activeWorkspaceId: string;

  workspaces: Workspace[];
  users: User[];
  workspaceMembers: WorkspaceMember[];
  teams: Team[];
  labels: Label[];
  issues: Issue[];
  projects: Project[];
  cycles: Cycle[];
  docs: Doc[];
  notes: Note[];
  progressCards: ProgressCard[];
  feedback: Feedback[];
  workspaceLinks: WorkspaceLink[];
  agentRuns: AgentRun[];
  agentSessions: AgentSession[];

  selectedIssueId: string | null;
  selectedDocId: string | null;
  selectedNoteId: string | null;
  selectedProgressCardId: string | null;
  selectedFeedbackId: string | null;
  activeTeamId: string | null;
  activeTag: string | null;
  commandOpen: boolean;
  createOpen: boolean;
  agentsModalRequest: number; // incremented to signal "open agents modal"
  sidebarCollapsed: boolean;

  init: () => Promise<void>;
  setHydrated: () => void;
  resetData: () => void;
  completeOnboarding: (version: number) => Promise<boolean>;
  refreshWorkspaceLiveData: (wsId: string) => Promise<void>;
  refreshAgentLiveData: (wsId: string) => Promise<void>;
  refreshWorkspaceMembers: (wsId: string) => Promise<void>;
  /** Hydrate one route target only; callers retain ownership of navigation. */
  ensureWorkspaceEntityTarget: (input: WorkspaceEntityTargetInput) => Promise<WorkspaceEntityTargetResult>;
  setActiveWorkspace: (id: string) => void;
  selectIssue: (id: string | null) => void;
  selectDoc: (id: string | null) => void;
  selectNote: (id: string | null) => void;

  createNote: (input: {
    body: string;
    tags?: string[];
    teamId?: string | null;
    projectId?: string | null;
    pinned?: boolean;
  }) => Note;
  updateNote: (id: string, patch: Partial<Note>) => void;
  deleteNote: (id: string) => void;
  selectProgressCard: (id: string | null) => void;
  setActiveTeam: (id: string | null) => void;
  setActiveTag: (tag: string | null) => void;
  setCommandOpen: (v: boolean) => void;
  requestAgentsModal: () => void;
  setCreateOpen: (v: boolean) => void;
  toggleSidebar: () => void;

  createWorkspace: (input: {
    name: string;
    icon?: string;
    color?: string;
    description?: string;
    category?: "client" | "dev" | "personal";
  }) => Promise<Workspace>;

  createIssue: (input: {
    title: string;
    description?: string;
    teamId: string;
    status?: StatusId;
    priority?: PriorityId;
    assigneeId?: string | null;
    ownerId?: string | null;
    executorAgentId?: string | null;
    requesterId?: string | null;
    reportChannel?: TaskReportChannel | null;
    reportContext?: string | null;
    reportUrl?: string | null;
    labelIds?: string[];
    projectId?: string | null;
    dueDate?: number | null;
    estimate?: number | null;
  }) => Issue;
  updateIssue: (id: string, patch: Partial<Issue>) => void;
  deleteIssue: (id: string) => void;
  addComment: (
    issueIdOrInput:
      | string
      | { parentType: "issue" | "doc" | "note"; parentId: string; body: string; replyToId?: string | null },
    body?: string,
    options?: { replyToId?: string | null }
  ) => Promise<void>;

  createProject: (input: {
    name: string;
    description?: string;
    teamIds: string[];
  }) => Project;
  updateProject: (id: string, patch: Partial<Project>) => void;

  createLabel: (input: { name: string; color: string; teamId: string }) => Label;

  createTeam: (input: {
    name: string;
    key: string;
    icon?: string;
    color?: string;
  }) => Team;

  createDoc: (input: {
    title: string;
    content?: string;
    icon?: string;
    projectId?: string | null;
    teamId?: string | null;
    parentId?: string | null;
    tags?: string[];
  }) => Doc;
  updateDoc: (id: string, patch: Partial<Doc>) => void;
  deleteDoc: (id: string) => void;

  createProgressCard: (input: {
    title: string;
    projectId?: string | null;
    teamId?: string | null;
    periodStart?: number;
    periodEnd?: number;
    summary?: string;
    highlights?: string[];
    blockers?: string[];
    nextSteps?: string[];
    metrics?: ProgressMetric[];
    issueIds?: string[];
  }) => ProgressCard;
  updateProgressCard: (id: string, patch: Partial<ProgressCard>) => void;
  deleteProgressCard: (id: string) => void;

  selectFeedback: (id: string | null) => void;
  submitFeedback: (input: {
    targetWorkspaceId: string;
    title: string;
    body?: string;
    severity?: FeedbackSeverity;
    tags?: string[];
    sourceDocId?: string | null;
  }) => Promise<Feedback | null>;
  triageFeedback: (id: string, action: {
    action: "convert_to_issue" | "link_existing_issue" | "wontfix" | "duplicate" | "resolve";
    teamId?: string;
    issueId?: string;
    status?: StatusId;
    priority?: PriorityId;
    assigneeId?: string | null;
    comment?: string;
  }) => Promise<void>;
  deleteFeedback: (id: string) => Promise<void>;
  linkWorkspaces: (
    workspaceId: string,
    targetWorkspaceId: string,
    direction?: "outbound" | "inbound"
  ) => Promise<boolean>;
  unlinkWorkspaces: (
    workspaceId: string,
    otherId: string,
    direction: "outbound" | "inbound"
  ) => Promise<boolean>;
}

export const useStore = create<State>()((set, get) => ({
  hydrated: false,
  loading: false,
  error: null,
  hydrationError: null,
  currentUserId: "",
  onboardingVersion: 0,
  onboardingCompletedAt: null,
  activeWorkspaceId: "",

  workspaces: [],
  users: [],
  workspaceMembers: [],
  teams: [],
  labels: [],
  issues: [],
  projects: [],
  cycles: [],
  docs: [],
  notes: [],
  progressCards: [],
  feedback: [],
  workspaceLinks: [],
  agentRuns: [],
  agentSessions: [],

  selectedIssueId: null,
  selectedDocId: null,
  selectedNoteId: null,
  selectedProgressCardId: null,
  selectedFeedbackId: null,
  activeTeamId: null,
  activeTag: null,
  commandOpen: false,
  createOpen: false,
  agentsModalRequest: 0,
  sidebarCollapsed: false,

  setHydrated: () => set({ hydrated: true }),
  resetData: () => {
    void get().init();
  },

  refreshWorkspaceMembers: async (wsId) => {
    if (!wsId) return;
    const sb = createClient();
    const { data, error } = await sb
      .from("workspace_members")
      .select("workspace_id,user_id,role,invited_by,joined_at")
      .eq("workspace_id", wsId);
    if (error) return;

    const memberIds = (data ?? []).map((member) => member.user_id);
    const teamIds = get()
      .teams.filter((team) => team.workspaceId === wsId)
      .map((team) => team.id);
    const [profilesResult, teamMembersResult] = await Promise.all([
      memberIds.length
        ? sb
            .from("profiles")
            .select(
              "id,name,email,initials,color,avatar_url,is_bot,bot_type,created_at,updated_at"
            )
            .in("id", memberIds)
        : Promise.resolve({ data: [], error: null }),
      teamIds.length
        ? sb
            .from("team_members")
            .select("team_id,user_id")
            .in("team_id", teamIds)
        : Promise.resolve({ data: [], error: null }),
    ]);

    const membersForWorkspace: WorkspaceMember[] = (data ?? []).map((m) => ({
      workspaceId: m.workspace_id,
      userId: m.user_id,
      role: m.role,
      invitedBy: m.invited_by,
      joinedAt: Date.parse(m.joined_at),
    }));
    const refreshedUsers = (profilesResult.data ?? []).map(mapProfile);
    const refreshedUserIds = new Set(refreshedUsers.map((user) => user.id));
    set((s) => ({
      workspaceMembers: [
        ...s.workspaceMembers.filter((m) => m.workspaceId !== wsId),
        ...membersForWorkspace,
      ],
      // Keep historical authors and people from other workspaces, but merge
      // newly provisioned profiles immediately so assignee pickers do not
      // require a full application reload.
      users: [
        ...s.users.filter((user) => !refreshedUserIds.has(user.id)),
        ...refreshedUsers,
      ],
      // A failed membership query must not make every team appear unjoined.
      // Preserve the prior snapshot until the next successful refresh.
      teams: teamMembersResult.error
        ? s.teams
        : replaceTeamMemberIdsForWorkspace(
            s.teams,
            wsId,
            teamMembersResult.data ?? []
          ),
    }));
  },

  // Coarse refetch of the live entities for ONE workspace — issues,
  // docs, notes, progress_cards, comments. Called by the realtime
  // subscription in Workspace.tsx whenever Postgres notifies of a change
  // on these tables. Replaces the workspace's slice in the local arrays
  // so other entries (other workspaces) remain untouched.
  refreshWorkspaceLiveData: async (wsId) => {
    if (!wsId) return;
    const sb = createClient();
    const [iss, lbl, dcs, pcs, nts, cms] = await Promise.all([
        sb
          .from("issues")
          .select(
            "id,identifier,workspace_id,team_id,title,description,status,is_blocked,priority,assignee_id,owner_id,executor_agent_id,requester_id,report_channel,report_context,report_url,project_id,cycle_id,parent_id,author_id,created_at,updated_at,completed_at,due_date,estimate,deleted_at,is_public"
          )
          .eq("workspace_id", wsId)
          .is("deleted_at", null),
        sb.from("issue_labels").select("*"),
        loadAllLiveDocs(sb, wsId),
        sb
          .from("progress_cards")
          .select(
            "id,workspace_id,project_id,team_id,title,status,period_start,period_end,summary,highlights,blockers,next_steps,metrics,issue_ids,author_id,source,external_key,source_refs,created_at,updated_at,deleted_at"
          )
          .eq("workspace_id", wsId)
          .is("deleted_at", null),
        sb
          .from("notes")
          .select(
            "id,workspace_id,project_id,team_id,author_id,body,tags,pinned,created_at,updated_at,deleted_at"
          )
          .eq("workspace_id", wsId)
          .is("deleted_at", null),
        sb
          .from("comments")
          .select("*")
          .order("created_at"),
      ]);
    const issueRows = iss.data ?? [];
    const labelRows = lbl.data ?? [];
    const commentRows = cms.data ?? [];
    const labelsByIssue: Record<string, string[]> = {};
    for (const r of labelRows as Array<{ issue_id: string; label_id: string }>) {
      (labelsByIssue[r.issue_id] ??= []).push(r.label_id);
    }
    const commentsByIssue: Record<string, typeof commentRows> = {};
    for (const c of commentRows as Array<{ issue_id: string }>) {
      (commentsByIssue[c.issue_id] ??= []).push(c as never);
    }
    const issuesForWs = issueRows.map((i) =>
      mapIssue(
        i as Parameters<typeof mapIssue>[0],
        labelsByIssue[i.id] ?? [],
        (commentsByIssue[i.id] ?? []) as Parameters<typeof mapIssue>[2]
      )
    );
    // Bucket doc + note comments from the same comments fetch (cms).
    const commentsByDoc: Record<string, Comment[]> = {};
    const commentsByNote: Record<string, Comment[]> = {};
    for (const c of (cms.data ?? []) as Array<{
      id: string;
      parent_type: string;
      parent_id: string;
      issue_id: string | null;
      author_id: string;
      body: string;
      created_at: string;
      reply_to_id?: string | null;
    }>) {
      const mapped: Comment = {
        id: c.id,
        parentType: c.parent_type as "issue" | "doc" | "note",
        parentId: c.parent_id,
        issueId: c.issue_id,
        userId: c.author_id,
        body: c.body,
        replyToId: c.reply_to_id ?? null,
        createdAt: Date.parse(c.created_at),
      };
      if (c.parent_type === "doc") {
        (commentsByDoc[c.parent_id] ??= []).push(mapped);
      } else if (c.parent_type === "note") {
        (commentsByNote[c.parent_id] ??= []).push(mapped);
      }
    }
    const docsForWs = dcs.map((d) => ({
      ...mapDoc(d),
      comments: commentsByDoc[d.id] ?? [],
    }));
    const progressCardsForWs = (pcs.data ?? []).map(mapProgressCard);
    const notesForWs = (nts.data ?? []).map((n) => ({
      ...mapNote(n),
      comments: commentsByNote[n.id] ?? [],
    }));
    // A target read may have returned its row before this snapshot but still
    // be awaiting relations. It must not restore that older row afterwards.
    advanceWorkspaceEntitySnapshot(wsId);
    set((s) => ({
      issues: [
        ...s.issues.filter((i) => i.workspaceId !== wsId),
        ...issuesForWs,
      ],
      docs: [
        ...s.docs.filter((d) => d.workspaceId !== wsId),
        ...docsForWs,
      ],
      progressCards: [
        ...s.progressCards.filter((p) => p.workspaceId !== wsId),
        ...progressCardsForWs,
      ],
      notes: [
        ...s.notes.filter((n) => n.workspaceId !== wsId),
        ...notesForWs,
      ],
    }));
  },

  refreshAgentLiveData: async (wsId) => {
    if (!wsId) return;
    const sb = createClient();
    const [activeRunsRes, recentRunsRes, sessionsRes] = await Promise.all([
      sb
        .from("agent_runs")
        .select(AGENT_RUN_COLUMNS)
        .eq("workspace_id", wsId)
        .eq("status", "in_progress")
        .order("created_at", { ascending: false })
        .limit(1_000),
      sb
        .from("agent_runs")
        .select(AGENT_RUN_COLUMNS)
        .eq("workspace_id", wsId)
        .neq("status", "in_progress")
        .order("created_at", { ascending: false })
        .limit(500),
      sb
        .from("agent_sessions")
        .select("*")
        .eq("workspace_id", wsId)
        .order("created_at", { ascending: false })
        .limit(200),
    ]);
    if (activeRunsRes.error || recentRunsRes.error || sessionsRes.error) return;
    const runsForWs = mapUniqueAgentRuns([
      ...(activeRunsRes.data ?? []),
      ...(recentRunsRes.data ?? []),
    ]);
    const sessionsForWs = (sessionsRes.data ?? []).map(mapAgentSession);
    set((s) => ({
      agentRuns: [
        ...s.agentRuns.filter((run) => run.workspaceId !== wsId),
        ...runsForWs,
      ],
      agentSessions: [
        ...s.agentSessions.filter((session) => session.workspaceId !== wsId),
        ...sessionsForWs,
      ],
    }));
  },

  init: async () => {
    set({ loading: true, error: null, hydrationError: null });
    try {
      const sb = createClient();
      const { data: auth, error: authError } = await sb.auth.getUser();
      if (authError) throw authError;
      if (!auth.user) {
        set({
          hydrated: true,
          loading: false,
          hydrationError: null,
          currentUserId: "",
          onboardingVersion: 0,
          onboardingCompletedAt: null,
        });
        return;
      }

      const [
        workspacesRes,
        workspaceMembersRes,
        teamsRes,
        teamMembersRes,
        labelsRes,
        projectsRes,
        projectTeamsRes,
        cyclesRes,
        issuesRes,
        issueLabelsRes,
        docsRes,
        cardsRes,
        notesRes,
        profilesRes,
        commentsRes,
        feedbackRes,
        linksRes,
      ] = await Promise.all([
        sb.from("workspaces").select("*").order("created_at"),
        sb
          .from("workspace_members")
          .select("workspace_id,user_id,role,invited_by,joined_at"),
        sb.from("teams").select("*").order("created_at"),
        sb.from("team_members").select("team_id,user_id"),
        sb.from("labels").select("*"),
        sb.from("projects").select("*").order("created_at", { ascending: false }),
        sb.from("project_teams").select("*"),
        sb.from("cycles").select("*"),
        // EXPLICIT column lists below: the search_doc tsvector column (added
        // for FTS) can be tens of KB per row — including it via select("*")
        // shipped MBs of useless data to the browser and was crashing Safari
        // on workspace switch. Search runs server-side via the workspace_search
        // RPC; the browser never needs the tsvector.
        sb
          .from("issues")
          .select(
            "id,identifier,workspace_id,team_id,title,description,status,is_blocked,priority,assignee_id,owner_id,executor_agent_id,requester_id,report_channel,report_context,report_url,project_id,cycle_id,parent_id,author_id,created_at,updated_at,completed_at,due_date,estimate,deleted_at,is_public"
          )
          .is("deleted_at", null)
          .order("updated_at", { ascending: false }),
        sb.from("issue_labels").select("*"),
        loadAllLiveDocs(sb),
        sb
          .from("progress_cards")
          .select(
            "id,workspace_id,project_id,team_id,title,status,period_start,period_end,summary,highlights,blockers,next_steps,metrics,issue_ids,author_id,source,external_key,source_refs,created_at,updated_at,deleted_at"
          )
          .is("deleted_at", null)
          .order("period_end", { ascending: false }),
        sb
          .from("notes")
          .select(
            "id,workspace_id,project_id,team_id,author_id,body,tags,pinned,created_at,updated_at,deleted_at"
          )
          .is("deleted_at", null)
          .order("created_at", { ascending: false }),
        sb
          .from("profiles")
          .select("id,name,email,initials,color,avatar_url,is_bot,bot_type,created_at,updated_at"),
        sb.from("comments").select("*").order("created_at"),
        sb
          .from("feedback")
          .select(
            "id,source_workspace_id,target_workspace_id,title,body,severity,status,tags,source_doc_id,submitter_id,linked_issue_id,triaged_by,triaged_at,resolved_at,created_at,updated_at"
          )
          .order("created_at", { ascending: false }),
        sb.from("workspace_links").select("*"),
      ]);

      // These rows establish the route workspace and signed-in principal.
      // Failed reads are not evidence that a workspace is absent/inaccessible.
      const identityFailure = [workspacesRes, workspaceMembersRes, profilesRes]
        .find((result) => result.error)?.error;
      if (identityFailure) throw identityFailure;

      const workspaces = (workspacesRes.data ?? []).map(mapWorkspace);
      const currentUserOnboarding = readOnboardingMetadata(
        auth.user.user_metadata
      );
      const users = (profilesRes.data ?? []).map(mapProfile);
      const workspaceMembers: WorkspaceMember[] = (workspaceMembersRes.data ?? []).map((m) => ({
        workspaceId: m.workspace_id,
        userId: m.user_id,
        role: m.role,
        invitedBy: m.invited_by,
        joinedAt: Date.parse(m.joined_at),
      }));
      const memberIdsByTeam = groupTeamMemberIds(teamMembersRes.data ?? []);
      const teams = (teamsRes.data ?? []).map((team) =>
        mapTeam(team, memberIdsByTeam[team.id] ?? [])
      );
      const labels = (labelsRes.data ?? []).map(mapLabel);
      const cycles = (cyclesRes.data ?? []).map(mapCycle);

      const teamsByProject: Record<string, string[]> = {};
      for (const pt of projectTeamsRes.data ?? []) {
        (teamsByProject[pt.project_id] ??= []).push(pt.team_id);
      }
      const projects = (projectsRes.data ?? []).map((p) =>
        mapProject(p, teamsByProject[p.id] ?? [])
      );

      const labelsByIssue: Record<string, string[]> = {};
      for (const il of issueLabelsRes.data ?? []) {
        (labelsByIssue[il.issue_id] ??= []).push(il.label_id);
      }
      const commentsByIssue: Record<string, typeof commentsRes.data> = {};
      for (const c of commentsRes.data ?? []) {
        // Polymorphic comments now — only issue-typed ones are
        // attached to issue rows. Doc/note comments live but are
        // handled per-entity (no doc/note hydration into issue list).
        if (c.parent_type !== "issue") continue;
        const key = c.parent_id;
        (commentsByIssue[key] ??= []).push(c);
      }
      const issues = (issuesRes.data ?? []).map((i) =>
        mapIssue(i, labelsByIssue[i.id] ?? [], commentsByIssue[i.id] ?? [])
      );

      // Doc + note comments come from the same commentsRes fetch above —
      // bucket by parent and attach to the entities (PAUT-31 polymorphic
      // surface).
      const commentsByDoc: Record<string, Comment[]> = {};
      const commentsByNote: Record<string, Comment[]> = {};
      for (const c of (commentsRes.data ?? []) as Array<{
        id: string;
        parent_type: string;
        parent_id: string;
        issue_id: string | null;
        author_id: string;
        body: string;
        created_at: string;
        reply_to_id?: string | null;
      }>) {
        if (c.parent_type !== "doc" && c.parent_type !== "note") continue;
        const m: Comment = {
          id: c.id,
          parentType: c.parent_type as "doc" | "note",
          parentId: c.parent_id,
          issueId: c.issue_id,
          userId: c.author_id,
          body: c.body,
          replyToId: c.reply_to_id ?? null,
          createdAt: Date.parse(c.created_at),
        };
        if (c.parent_type === "doc") (commentsByDoc[c.parent_id] ??= []).push(m);
        else (commentsByNote[c.parent_id] ??= []).push(m);
      }
      const docs = docsRes.map((d) => ({
        ...mapDoc(d),
        comments: commentsByDoc[d.id] ?? [],
      }));
      const progressCards = (cardsRes.data ?? []).map(mapProgressCard);
      const notes = (notesRes.data ?? []).map((n) => ({
        ...mapNote(n),
        comments: commentsByNote[n.id] ?? [],
      }));
      const feedback = (feedbackRes.data ?? []).map(mapFeedback);
      const workspaceLinks = (linksRes.data ?? []).map(mapWorkspaceLink);

      // Prefer the workspace the user was last looking at (persisted in
      // localStorage). Fall back to workspaces[0] only if that workspace
      // no longer exists or is the user's first session.
      const savedId =
        typeof window !== "undefined"
          ? window.localStorage.getItem("pauta:activeWorkspaceId")
          : null;
      const activeWs =
        (savedId && workspaces.find((w) => w.id === savedId)) || workspaces[0];
      // Same treatment for the team within the active workspace — without
      // this, a refresh always lands on the first team (e.g. 77Sol inside
      // Littera) regardless of where the user was when they hit reload.
      const savedTeamId =
        activeWs && typeof window !== "undefined"
          ? window.localStorage.getItem(
              `pauta:activeTeamId:${activeWs.id}`
            )
          : null;
      const teamsInWs = activeWs
        ? teams.filter((t) => t.workspaceId === activeWs.id)
        : [];
      const activeTeam = preferredTeamForUser(
        teamsInWs,
        savedTeamId,
        auth.user.id
      );
      let agentRuns: AgentRun[] = [];
      let agentSessions: AgentSession[] = [];
      if (activeWs) {
        const [activeRunsRes, recentRunsRes, sessionsRes] = await Promise.all([
          sb
            .from("agent_runs")
            .select(AGENT_RUN_COLUMNS)
            .eq("workspace_id", activeWs.id)
            .eq("status", "in_progress")
            .order("created_at", { ascending: false })
            .limit(1_000),
          sb
            .from("agent_runs")
            .select(AGENT_RUN_COLUMNS)
            .eq("workspace_id", activeWs.id)
            .neq("status", "in_progress")
            .order("created_at", { ascending: false })
            .limit(500),
          sb
            .from("agent_sessions")
            .select(
              "id,workspace_id,author_id,name,summary,next_steps,open_questions,touched_issue_ids,started_at,ended_at,context,external_session_key,created_at,updated_at"
            )
            .eq("workspace_id", activeWs.id)
            .order("started_at", { ascending: false })
            .limit(200),
        ]);
        agentRuns = mapUniqueAgentRuns([
          ...(activeRunsRes.data ?? []),
          ...(recentRunsRes.data ?? []),
        ]);
        agentSessions = (sessionsRes.data ?? []).map(mapAgentSession);
      }

      // Bootstrap replaces the complete entity inventory, including workspaces
      // removed since the previous snapshot. Invalidate in-flight reads for both.
      for (const workspaceId of new Set([
        ...get().workspaces.map((workspace) => workspace.id),
        ...workspaces.map((workspace) => workspace.id),
      ])) advanceWorkspaceEntitySnapshot(workspaceId);
      set({
        currentUserId: auth.user.id,
        onboardingVersion: currentUserOnboarding.version,
        onboardingCompletedAt: currentUserOnboarding.completedAt,
        workspaces,
        users,
        workspaceMembers,
        teams,
        labels,
        projects,
        cycles,
        issues,
        docs,
        notes,
        progressCards,
        feedback,
        workspaceLinks,
        agentRuns,
        agentSessions,
        activeWorkspaceId: activeWs?.id ?? "",
        activeTeamId: activeTeam?.id ?? null,
        loading: false,
        hydrated: true,
        hydrationError: null,
      });
    } catch (e) {
      set({
        loading: false,
        hydrated: true,
        error: e instanceof Error ? e.message : "init failed",
        hydrationError: e instanceof Error ? e.message : "init failed",
      });
    }
  },

  setActiveWorkspace: (id) => {
    const teamsInWs = get().teams.filter((t) => t.workspaceId === id);
    const savedTeamId =
      typeof window !== "undefined"
        ? window.localStorage.getItem(`pauta:activeTeamId:${id}`)
        : null;
    const restoredTeam = preferredTeamForUser(
      teamsInWs,
      savedTeamId,
      get().currentUserId
    );
    if (typeof window !== "undefined") {
      window.localStorage.setItem("pauta:activeWorkspaceId", id);
    }
    set({
      activeWorkspaceId: id,
      activeTeamId: restoredTeam?.id ?? null,
      selectedIssueId: null,
      selectedDocId: null,
      selectedNoteId: null,
      selectedProgressCardId: null,
    });
  },

  ensureWorkspaceEntityTarget: async (input) => {
    const userId = get().currentUserId;
    const workspaceId = input.workspaceId.toLowerCase();
    if (!userId || get().activeWorkspaceId !== workspaceId || input.signal?.aborted ||
      !get().workspaces.some((workspace) => workspace.id === workspaceId)) return { status: "error" };
    const request = ++workspaceEntityTargetRequest;
    const snapshotRevision = workspaceEntitySnapshotRevisions.get(workspaceId) ?? 0;
    let contextChanged = false;
    // Remember switches away and back too, rather than comparing only the
    // final values after a slow request completes.
    const unsubscribe = useStore.subscribe((state) => {
      if (state.currentUserId !== userId || state.activeWorkspaceId !== workspaceId) contextChanged = true;
    });
    try {
      const result = await loadWorkspaceEntityTarget(input);
      if (contextChanged || input.signal?.aborted || request !== workspaceEntityTargetRequest ||
        (workspaceEntitySnapshotRevisions.get(workspaceId) ?? 0) !== snapshotRevision ||
        get().currentUserId !== userId || get().activeWorkspaceId !== workspaceId ||
        !get().workspaces.some((workspace) => workspace.id === workspaceId)) return { status: "error" };
      if (result.status !== "found") return result;
      if (result.kind !== input.kind || result.entity.id !== input.id.toLowerCase() ||
        result.entity.workspaceId !== workspaceId) return { status: "unavailable" };
      if (result.kind === "issue") {
        set((state) => ({ issues: [...state.issues.filter((issue) => issue.id !== result.entity.id), result.entity] }));
      } else if (result.kind === "doc") {
        set((state) => ({ docs: [...state.docs.filter((doc) => doc.id !== result.entity.id), result.entity] }));
      } else {
        set((state) => ({ notes: [...state.notes.filter((note) => note.id !== result.entity.id), result.entity] }));
      }
      return result;
    } finally {
      unsubscribe();
    }
  },

  completeOnboarding: async (version) => {
    const currentUserId = get().currentUserId;
    if (!currentUserId || !Number.isInteger(version) || version <= 0) {
      return false;
    }

    const sb = createClient();
    const persisted = await persistOnboardingCompletion(
      sb,
      currentUserId,
      version
    );
    if (!persisted || get().currentUserId !== currentUserId) return false;

    set((state) =>
      state.currentUserId !== currentUserId ||
      state.onboardingVersion > persisted.version
        ? {}
        : {
            onboardingVersion: persisted.version,
            onboardingCompletedAt:
              state.onboardingVersion === persisted.version &&
              state.onboardingCompletedAt !== null
                ? state.onboardingCompletedAt
                : persisted.completedAt,
          }
    );
    return true;
  },

  selectIssue: (id) => set({ selectedIssueId: id }),
  selectDoc: (id) => set({ selectedDocId: id }),
  selectNote: (id) => set({ selectedNoteId: id }),
  selectProgressCard: (id) => set({ selectedProgressCardId: id }),
  setActiveTeam: (id) => {
    const wsId = get().activeWorkspaceId;
    if (id && wsId && typeof window !== "undefined") {
      window.localStorage.setItem(`pauta:activeTeamId:${wsId}`, id);
    }
    set({ activeTeamId: id });
  },
  setActiveTag: (tag) => set({ activeTag: tag }),
  setCommandOpen: (v) => set({ commandOpen: v }),
  setCreateOpen: (v) => set({ createOpen: v }),
  requestAgentsModal: () =>
    set((s) => ({ agentsModalRequest: s.agentsModalRequest + 1 })),
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

  createWorkspace: async (input) => {
    // Route through server-side /api/workspaces so RLS sees auth.uid()
    // cleanly from SSR cookies, instead of relying on the browser client
    // JWT attachment.
    const res = await fetch("/api/workspaces", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    const data = (await res.json().catch(() => null)) as {
      ok?: boolean;
      workspace?: Parameters<typeof mapWorkspace>[0];
      error?: string;
      code?: string;
      details?: string;
      hint?: string;
    } | null;
    if (!res.ok || !data?.ok || !data.workspace) {
      const msg = data?.error ?? `HTTP ${res.status}`;
      const err = new Error(msg) as Error & {
        code?: string;
        details?: string;
        hint?: string;
      };
      if (data?.code) err.code = data.code;
      if (data?.details) err.details = data.details;
      if (data?.hint) err.hint = data.hint;
      throw err;
    }
    const ws = mapWorkspace(data.workspace);
    set((s) => ({ workspaces: [...s.workspaces, ws] }));
    return ws;
  },

  createIssue: (input) => {
    const team = get().teams.find((t) => t.id === input.teamId);
    if (!team) throw new Error("Team not found");
    const origin = parseTaskReportOrigin({
      report_channel: input.reportChannel === undefined ? "pauta" : input.reportChannel,
      report_context: input.reportContext ?? null,
      report_url: input.reportUrl ?? null,
    });
    if (!origin.ok) throw new Error(origin.error);
    const currentHuman = get().users.find((user) => user.id === get().currentUserId && !user.isBot);
    const requesterId = input.requesterId !== undefined ? input.requesterId : currentHuman?.id ?? null;
    const legacyAssignee = input.assigneeId ?? null;
    const legacyProfile = legacyAssignee
      ? get().users.find((user) => user.id === legacyAssignee)
      : null;
    const ownerId =
      input.ownerId !== undefined
        ? input.ownerId
        : legacyProfile?.isBot
          ? null
          : legacyAssignee;
    const executorAgentId =
      input.executorAgentId !== undefined
        ? input.executorAgentId
        : legacyProfile?.isBot
          ? legacyAssignee
          : null;
    const compatibilityAssigneeId = executorAgentId ?? ownerId;
    const now = Date.now();
    const localId = uid("iss");
    const optimistic: Issue = {
      isPublic: false,
      id: localId,
      identifier: `${team.key}-?`,
      workspaceId: team.workspaceId,
      teamId: input.teamId,
      title: input.title,
      description: input.description || "",
      status: input.status || "todo",
      isBlocked: false,
      priority: input.priority ?? 0,
      assigneeId: compatibilityAssigneeId,
      ownerId,
      executorAgentId,
      labelIds: input.labelIds || [],
      projectId: input.projectId ?? null,
      cycleId: null,
      createdAt: now,
      updatedAt: now,
      completedAt: (input.status ?? "todo") === "done" ? now : null,
      dueDate: input.dueDate ?? null,
      estimate: input.estimate ?? null,
      parentId: null,
      authorId: get().currentUserId,
      // The authenticated human is the default reporter; agents must name
      // their human requester explicitly rather than becoming the reporter.
      requesterId,
      reportChannel: origin.value.report_channel,
      reportContext: origin.value.report_context,
      reportUrl: origin.value.report_url,
      subscribers: ownerId ? [ownerId] : [],
      comments: [],
    };
    set((s) => ({ issues: [optimistic, ...s.issues] }));

    const sb = createClient();
    void (async () => {
      const { data: me } = await sb.auth.getUser();
      const { data, error } = await sb
        .from("issues")
        .insert({
          workspace_id: team.workspaceId,
          team_id: input.teamId,
          title: input.title,
          description: input.description ?? "",
          status: input.status ?? "todo",
          priority: PRIORITY_TO_DB[input.priority ?? 0],
          assignee_id: compatibilityAssigneeId,
          owner_id: ownerId,
          executor_agent_id: executorAgentId,
          requester_id: requesterId,
          ...origin.value,
          project_id: input.projectId ?? null,
          due_date: input.dueDate ? new Date(input.dueDate).toISOString() : null,
          estimate: input.estimate ?? null,
          author_id: me.user?.id ?? null,
          identifier: "",
        })
        .select()
        .single();
      if (error || !data) {
        set((s) => ({ issues: s.issues.filter((i) => i.id !== localId) }));
        return;
      }
      if (input.labelIds?.length) {
        await sb.from("issue_labels").insert(
          input.labelIds.map((lid) => ({ issue_id: data.id, label_id: lid }))
        );
      }
      const real = mapIssue(data, input.labelIds ?? [], []);
      set((s) => ({
        issues: s.issues.map((i) => (i.id === localId ? real : i)),
      }));
    })();

    return optimistic;
  },

  updateIssue: (id, patch) => {
    const current = get().issues.find((issue) => issue.id === id);
    const userId = get().currentUserId;
    if (!current || !userId || current.workspaceId !== get().activeWorkspaceId) return;
    // Report provenance is fixed at creation, including unknown legacy origin.
    const { reportChannel, reportContext, reportUrl, ...mutablePatch } = patch;
    void reportChannel;
    void reportContext;
    void reportUrl;
    let normalizedPatch = mutablePatch;
    if (current) {
      let ownerId = patch.ownerId !== undefined ? patch.ownerId : current.ownerId;
      let executorAgentId =
        patch.executorAgentId !== undefined
          ? patch.executorAgentId
          : current.executorAgentId;
      if (
        patch.assigneeId !== undefined &&
        patch.ownerId === undefined &&
        patch.executorAgentId === undefined
      ) {
        const legacyProfile = patch.assigneeId
          ? get().users.find((user) => user.id === patch.assigneeId)
          : null;
        if (!patch.assigneeId) {
          ownerId = null;
          executorAgentId = null;
        } else if (legacyProfile?.isBot) {
          executorAgentId = patch.assigneeId;
        } else {
          ownerId = patch.assigneeId;
          executorAgentId = null;
        }
      }
      normalizedPatch = {
        ...mutablePatch,
        ownerId,
        executorAgentId,
        assigneeId: executorAgentId ?? ownerId,
      };
    }
    const workspaceId = current.workspaceId;
    const editKey = JSON.stringify([workspaceId, userId, id]);
    const editVersion = Symbol();
    taskEditVersions.set(editKey, editVersion);
    const finishWrite = beginTaskWrite(workspaceId, userId, id);
    let optimistic: Issue | undefined;
    let reconcile = false;
    let issuePersisted = false;
    let identityChanged = false;
    let unsubscribeIdentity = () => {};
    let sb: ReturnType<typeof createClient>;
    try {
      sb = createClient();
      const { data: { subscription } } = sb.auth.onAuthStateChange((_event, session) => {
        if (session?.user?.id !== userId) identityChanged = true;
      });
      unsubscribeIdentity = () => subscription.unsubscribe();
    } catch (error) {
      finishWrite();
      throw error;
    }
    const sameScope = () => !identityChanged && get().activeWorkspaceId === workspaceId && get().currentUserId === userId;
    const verifyIdentity = async () => {
      if (!sameScope()) return false;
      const { data, error } = await sb.auth.getUser();
      if (data.user?.id !== userId) identityChanged = true;
      return !error && data.user?.id === userId && sameScope();
    };
    const sameMetadata = (left: Issue, right: Issue) => {
      const related = new Set(["comments", "labelIds", "subscribers"]);
      return [...new Set([...Object.keys(left), ...Object.keys(right)])]
        .filter((key) => !related.has(key))
        .every((key) => Object.is(left[key as keyof Issue], right[key as keyof Issue]));
    };
    const editedKeys = [...new Set<keyof Issue>([...Object.keys(normalizedPatch) as (keyof Issue)[], "completedAt"])];
    const stillOptimistic = (task: Issue) => optimistic && editedKeys.every((key) => Object.is(task[key], optimistic![key]));
    const rollback = () => {
      if (!current || !sameScope()) return;
      if (taskEditVersions.get(editKey) !== editVersion) { reconcile = true; return; }
      set((state) => ({ issues: state.issues.map((task) => {
        if (task.id !== id || task.workspaceId !== workspaceId) return task;
        if (!stillOptimistic(task)) { reconcile = true; return task; }
        // Revert this edit's fields only; comments/subscribers can arrive while
        // the request is in flight and are not part of the rejected mutation.
        const restored = Object.fromEntries(editedKeys.map((key) => [key, current[key]]));
        return { ...task, ...restored, updatedAt: task.updatedAt === optimistic?.updatedAt ? current.updatedAt : task.updatedAt };
      }) }));
    };
    const publishRow = (row: Database["public"]["Tables"]["issues"]["Row"], version: symbol, expected: Issue | undefined) => {
      if (!sameScope() || taskEditVersions.get(editKey) !== version || row.id !== id || row.workspace_id !== workspaceId ||
        row.deleted_at !== null || !Number.isFinite(Date.parse(row.updated_at))) return false;
      let published = false;
      set((state) => ({ issues: state.issues.map((task) => {
        if (task.id !== id || task.workspaceId !== workspaceId || !expected || !sameMetadata(task, expected)) return task;
        published = true;
        return { ...mapIssue(row, task.labelIds), comments: task.comments, subscribers: task.subscribers };
      }) }));
      return published;
    };
    const reconcileAfterWrites = async () => {
      try {
        await waitForTaskWrites(workspaceId, userId);
        if (!sameScope()) return;
        const version = taskEditVersions.get(editKey);
        if (!await verifyIdentity() || taskEditVersions.get(editKey) !== version) return;
        const expected = get().issues.find((task) => task.id === id && task.workspaceId === workspaceId);
        if (!expected) return;
        const { data: row, error } = await sb.from("issues").select("*")
          .eq("id", id).eq("workspace_id", workspaceId).is("deleted_at", null).maybeSingle();
        if (error || !await verifyIdentity() || taskEditVersions.get(editKey) !== version) return;
        if (row && version) publishRow(row, version, expected);
        else if (!row) set((state) => ({ issues: state.issues.filter((task) =>
          task.id !== id || task.workspaceId !== workspaceId || !sameMetadata(task, expected)) }));
      } catch { /* A failed reconciliation leaves newer local edits intact. */ }
    };
    try {
      if (!sameScope()) { finishWrite(); unsubscribeIdentity(); return; }
      set((s) => ({
        issues: s.issues.map((i) => {
          if (i.id !== id) return i;
          const now = Date.now();
          optimistic = {
            ...i,
            ...normalizedPatch,
            updatedAt: now,
            completedAt:
              normalizedPatch.status === "done" && i.status !== "done"
                ? now
                : normalizedPatch.status !== undefined && normalizedPatch.status !== "done"
                  ? null
                  : i.completedAt,
          };
          return optimistic;
        }),
      }));
      if (!sameScope()) { finishWrite(); unsubscribeIdentity(); return; }
      void (async () => {
        try {
          if (!await verifyIdentity()) { rollback(); return; }
          const dbPatch: Database["public"]["Tables"]["issues"]["Update"] = {};
          if (normalizedPatch.title !== undefined) dbPatch.title = normalizedPatch.title;
          if (normalizedPatch.description !== undefined) dbPatch.description = normalizedPatch.description;
          if (normalizedPatch.status !== undefined) dbPatch.status = normalizedPatch.status;
          if (normalizedPatch.isBlocked !== undefined) dbPatch.is_blocked = normalizedPatch.isBlocked;
          if (normalizedPatch.priority !== undefined) dbPatch.priority = PRIORITY_TO_DB[normalizedPatch.priority];
          if (normalizedPatch.assigneeId !== undefined) dbPatch.assignee_id = normalizedPatch.assigneeId;
          if (normalizedPatch.ownerId !== undefined) dbPatch.owner_id = normalizedPatch.ownerId;
          if (normalizedPatch.executorAgentId !== undefined)
            dbPatch.executor_agent_id = normalizedPatch.executorAgentId;
          if (normalizedPatch.requesterId !== undefined) dbPatch.requester_id = normalizedPatch.requesterId;
          if (normalizedPatch.projectId !== undefined) dbPatch.project_id = normalizedPatch.projectId;
          if (normalizedPatch.dueDate !== undefined)
            dbPatch.due_date = normalizedPatch.dueDate
              ? new Date(normalizedPatch.dueDate).toISOString()
              : null;
          if (normalizedPatch.estimate !== undefined) dbPatch.estimate = normalizedPatch.estimate;

          if (Object.keys(dbPatch).length > 0) {
            const { data: row, error } = await sb.from("issues").update(dbPatch)
              .eq("id", id).eq("workspace_id", workspaceId).select("*").maybeSingle();
            if (!await verifyIdentity()) return;
            if (error || !row || row.id !== id || row.workspace_id !== workspaceId || row.deleted_at !== null) {
              rollback();
              return;
            }
            issuePersisted = true;
            if (!publishRow(row, editVersion, optimistic)) reconcile = true;
          }
          if (normalizedPatch.labelIds !== undefined) {
            if (!await verifyIdentity()) return;
            await sb.from("issue_labels").delete().eq("issue_id", id);
            if (normalizedPatch.labelIds.length) {
              if (!await verifyIdentity()) return;
              await sb.from("issue_labels").insert(
                normalizedPatch.labelIds.map((lid) => ({ issue_id: id, label_id: lid }))
              );
            }
          }
        } catch {
          if (!issuePersisted) rollback();
          else reconcile = true;
        } finally {
          finishWrite();
          if (reconcile) void reconcileAfterWrites().finally(unsubscribeIdentity);
          else unsubscribeIdentity();
        }
      })();
    } catch (error) {
      rollback();
      finishWrite();
      if (reconcile) void reconcileAfterWrites().finally(unsubscribeIdentity);
      else unsubscribeIdentity();
      throw error;
    }
  },

  deleteIssue: (id) => {
    // Soft delete: drop from local view + set deleted_at in DB.
    // Trash panel surfaces deleted rows; restore via the trash UI.
    const snapshot = get().issues.find((i) => i.id === id) ?? null;
    const userId = get().currentUserId;
    const finishWrite = beginTaskWrite(snapshot?.workspaceId ?? get().activeWorkspaceId, userId, id);
    try {
      set((s) => ({
        issues: s.issues.filter((i) => i.id !== id),
        selectedIssueId: s.selectedIssueId === id ? null : s.selectedIssueId,
      }));
      const sb = createClient();
      void Promise.resolve(sb
        .from("issues")
        .update({ deleted_at: new Date().toISOString(), updated_by: userId })
        .eq("id", id)
        .then(({ error }) => {
          if (!error) return;
          console.error("[deleteIssue] failed, restoring:", error);
          if (snapshot) set((s) => ({ issues: [...s.issues, snapshot] }));
        })).finally(finishWrite);
    } catch (error) {
      finishWrite();
      throw error;
    }
  },

  // Polymorphic comment add. Two call shapes for back-compat:
  //   addComment(issueId, body) — legacy, treated as issue comment
  //   addComment({ parentType, parentId, body }) — explicit
  addComment: async (issueIdOrInput, body, options) => {
    const input = typeof issueIdOrInput === "string"
      ? { parentType: "issue" as const, parentId: issueIdOrInput, body: body ?? "", replyToId: options?.replyToId ?? null }
      : issueIdOrInput;
    const { currentUserId: userId, activeWorkspaceId: workspaceId } = get();
    const collection = input.parentType === "issue" ? "issues" : input.parentType === "doc" ? "docs" : "notes";
    const parent = get()[collection].find((entity) => entity.id === input.parentId && entity.workspaceId === workspaceId);
    if (!parent || !userId || !input.body.trim()) throw new Error("Comment target unavailable");
    const replyToId = input.replyToId ?? null;
    if (replyToId && !(parent.comments ?? []).some((comment) => comment.id === replyToId && comment.parentId === input.parentId && comment.parentType === input.parentType)) throw new Error("Reply target unavailable");
    const localComment: Comment = { id: uid("cm"), parentType: input.parentType, parentId: input.parentId,
      issueId: input.parentType === "issue" ? input.parentId : null, userId, body: input.body, replyToId, createdAt: Date.now() };
    let invalidIdentity = false;
    const sameScope = () => !invalidIdentity && get().activeWorkspaceId === workspaceId && get().currentUserId === userId;
    const unsubscribeScope = useStore.subscribe((state) => { if (state.activeWorkspaceId !== workspaceId || state.currentUserId !== userId) invalidIdentity = true; });
    const replaceLocal = (persistedId: string | null) => {
      if (!sameScope()) return;
      set((state) => ({ [collection]: state[collection].map((entity) => entity.id === input.parentId && entity.workspaceId === workspaceId
        ? { ...entity, comments: (entity.comments ?? []).flatMap((comment) => comment.id === localComment.id ? (persistedId && !(entity.comments ?? []).some((canonical) => canonical.id === persistedId) ? [{ ...comment, id: persistedId }] : []) : comment) }
        : entity) }));
    };
    const sb = createClient();
    const { data: listener } = sb.auth.onAuthStateChange((_event, session) => { if (session?.user?.id !== userId) invalidIdentity = true; });
    try {
      const { data: identity, error: authError } = await sb.auth.getUser();
      if (authError || invalidIdentity || identity.user?.id !== userId || !sameScope()) throw new Error("Session changed");
      set((state) => ({ [collection]: state[collection].map((entity) => entity.id === input.parentId && entity.workspaceId === workspaceId
        ? { ...entity, comments: [...(entity.comments ?? []), localComment] } : entity) }));
      const { data, error } = await sb.from("comments").insert({ parent_type: input.parentType, parent_id: input.parentId,
        author_id: userId, body: input.body, reply_to_id: replyToId }).select("id").single();
      if (invalidIdentity || !sameScope()) throw new Error("Session changed");
      if (error || !data?.id) throw new Error("Comment not confirmed");
      replaceLocal(data.id);
      if (/@[a-z0-9][a-z0-9_-]{1,40}/i.test(input.body)) void fetch("/api/internal/notify-comment-mention", {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ comment_id: data.id }),
      }).catch(() => {});
    } catch (error) { replaceLocal(null); throw error; }
    finally { listener.subscription.unsubscribe(); unsubscribeScope(); }
  },

  createProject: (input) => {
    const wsId = get().activeWorkspaceId;
    const localId = uid("p");
    const now = Date.now();
    const project: Project = {
      id: localId,
      workspaceId: wsId,
      name: input.name,
      description: input.description || "",
      teamIds: input.teamIds,
      leadId: get().currentUserId,
      status: "planned",
      icon: "✦",
      color: "#5e6ad2",
      startDate: null,
      targetDate: null,
      createdAt: now,
      updatedAt: now,
    };
    set((s) => ({ projects: [project, ...s.projects] }));
    const sb = createClient();
    void (async () => {
      const { data, error } = await sb
        .from("projects")
        .insert({
          workspace_id: wsId,
          name: input.name,
          description: input.description ?? "",
          status: "planned",
          icon: "✦",
          color: "#5e6ad2",
          lead_id: get().currentUserId,
        })
        .select()
        .single();
      if (error || !data) {
        set((s) => ({ projects: s.projects.filter((p) => p.id !== localId) }));
        return;
      }
      if (input.teamIds.length) {
        await sb.from("project_teams").insert(
          input.teamIds.map((tid) => ({ project_id: data.id, team_id: tid }))
        );
      }
      const real = mapProject(data, input.teamIds);
      set((s) => ({
        projects: s.projects.map((p) => (p.id === localId ? real : p)),
      }));
    })();
    return project;
  },

  updateProject: (id, patch) => {
    set((s) => ({
      projects: s.projects.map((p) =>
        p.id === id ? { ...p, ...patch, updatedAt: Date.now() } : p
      ),
    }));
    const sb = createClient();
    void (async () => {
      const dbPatch: Database["public"]["Tables"]["projects"]["Update"] = {};
      if (patch.name !== undefined) dbPatch.name = patch.name;
      if (patch.description !== undefined) dbPatch.description = patch.description;
      if (patch.status !== undefined) dbPatch.status = patch.status;
      if (patch.icon !== undefined) dbPatch.icon = patch.icon;
      if (patch.color !== undefined) dbPatch.color = patch.color;
      if (patch.leadId !== undefined) dbPatch.lead_id = patch.leadId;
      if (patch.startDate !== undefined)
        dbPatch.start_date = patch.startDate ? new Date(patch.startDate).toISOString() : null;
      if (patch.targetDate !== undefined)
        dbPatch.target_date = patch.targetDate ? new Date(patch.targetDate).toISOString() : null;
      if (Object.keys(dbPatch).length) {
        await sb.from("projects").update(dbPatch).eq("id", id);
      }
      if (patch.teamIds !== undefined) {
        await sb.from("project_teams").delete().eq("project_id", id);
        if (patch.teamIds.length) {
          await sb.from("project_teams").insert(
            patch.teamIds.map((teamId) => ({ project_id: id, team_id: teamId }))
          );
        }
      }
    })();
  },

  createLabel: (input) => {
    const localId = uid("l");
    const label: Label = { id: localId, ...input };
    set((s) => ({ labels: [...s.labels, label] }));
    const sb = createClient();
    void (async () => {
      const { data } = await sb
        .from("labels")
        .insert({ team_id: input.teamId, name: input.name, color: input.color })
        .select()
        .single();
      if (data) {
        const real = mapLabel(data);
        set((s) => ({
          labels: s.labels.map((l) => (l.id === localId ? real : l)),
        }));
      }
    })();
    return label;
  },

  createTeam: (input) => {
    const wsId = get().activeWorkspaceId;
    const creatorId = get().currentUserId;
    const localId = uid("t");
    const team: Team = {
      id: localId,
      workspaceId: wsId,
      key: input.key.toUpperCase().slice(0, 4),
      name: input.name,
      icon: input.icon || "◆",
      color: input.color || "#5e6ad2",
      memberIds: creatorId ? [creatorId] : [],
    };
    set((s) => ({ teams: [...s.teams, team] }));
    const sb = createClient();
    void (async () => {
      const { data, error: teamError } = await sb
        .from("teams")
        .insert({
          workspace_id: wsId,
          key: team.key,
          name: input.name,
          icon: team.icon,
          color: team.color,
          description: "",
        })
        .select()
        .single();
      if (teamError || !data) {
        set((s) => ({
          teams: s.teams.filter((t) => t.id !== localId),
          error: teamError?.message ?? "Failed to create team",
        }));
        return;
      }

      const { error: membershipError } = creatorId
        ? await sb
            .from("team_members")
            .upsert(
              { team_id: data.id, user_id: creatorId },
              { onConflict: "team_id,user_id", ignoreDuplicates: true }
            )
        : { error: new Error("No signed-in user available for team membership") };

      if (membershipError) {
        // Keep the optimistic UI and database aligned. Team creation and its
        // creator membership are one logical operation even though Supabase
        // exposes them as two writes here.
        const { error: rollbackError } = await sb
          .from("teams")
          .delete()
          .eq("id", data.id);
        set((s) => ({
          teams: s.teams.filter((t) => t.id !== localId),
          error: rollbackError
            ? `${membershipError.message}; team rollback failed: ${rollbackError.message}`
            : membershipError.message,
        }));
        return;
      }

      const real = mapTeam(data, [creatorId]);
      set((s) => ({
        teams: s.teams.map((t) => (t.id === localId ? real : t)),
      }));
    })();
    return team;
  },

  createDoc: (input) => {
    const wsId = get().activeWorkspaceId;
    const localId = uid("doc");
    const doc: Doc = {
      isPublic: false,
      id: localId,
      workspaceId: wsId,
      projectId: input.projectId ?? null,
      teamId: input.teamId ?? null,
      parentId: input.parentId ?? null,
      position: 0,
      title: input.title,
      content: input.content || "",
      icon: input.icon || "📄",
      authorId: get().currentUserId,
      tags: input.tags || [],
      pinned: false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    set((s) => ({ docs: [doc, ...s.docs] }));
    const sb = createClient();
    void (async () => {
      const { data } = await sb
        .from("docs")
        .insert({
          workspace_id: wsId,
          project_id: input.projectId ?? null,
          team_id: input.teamId ?? null,
          parent_id: input.parentId ?? null,
          title: input.title,
          content: input.content ?? "",
          icon: input.icon ?? "📄",
          tags: input.tags ?? [],
          author_id: get().currentUserId,
        })
        .select()
        .single();
      if (data) {
        const real = mapDoc(data);
        set((s) => ({ docs: s.docs.map((d) => (d.id === localId ? real : d)) }));
      }
    })();
    return doc;
  },

  updateDoc: (id, patch) => {
    set((s) => ({
      docs: s.docs.map((d) =>
        d.id === id ? { ...d, ...patch, updatedAt: Date.now() } : d
      ),
    }));
    const sb = createClient();
    void (async () => {
      const dbPatch: Database["public"]["Tables"]["docs"]["Update"] = {};
      if (patch.title !== undefined) dbPatch.title = patch.title;
      if (patch.content !== undefined) dbPatch.content = patch.content;
      if (patch.icon !== undefined) dbPatch.icon = patch.icon;
      if (patch.tags !== undefined) dbPatch.tags = patch.tags;
      if (patch.pinned !== undefined) dbPatch.pinned = patch.pinned;
      if (patch.projectId !== undefined) dbPatch.project_id = patch.projectId;
      if (patch.teamId !== undefined) dbPatch.team_id = patch.teamId;
      if (patch.parentId !== undefined) dbPatch.parent_id = patch.parentId;
      if (patch.position !== undefined) dbPatch.position = patch.position;
      if (Object.keys(dbPatch).length) {
        await sb.from("docs").update(dbPatch).eq("id", id);
      }
    })();
  },

  deleteDoc: (id) => {
    const snapshot = get().docs.find((d) => d.id === id) ?? null;
    set((s) => ({
      docs: s.docs.filter((d) => d.id !== id),
      selectedDocId: s.selectedDocId === id ? null : s.selectedDocId,
    }));
    const sb = createClient();
    sb
      .from("docs")
      .update({ deleted_at: new Date().toISOString(), updated_by: get().currentUserId })
      .eq("id", id)
      .then(({ error }) => {
        if (!error) return;
        console.error("[deleteDoc] failed, restoring:", error);
        if (snapshot) set((s) => ({ docs: [...s.docs, snapshot] }));
      });
  },

  createProgressCard: (input) => {
    const now = Date.now();
    const wsId = get().activeWorkspaceId;
    const localId = uid("pc");
    const card: ProgressCard = {
      id: localId,
      workspaceId: wsId,
      projectId: input.projectId ?? null,
      teamId: input.teamId ?? null,
      title: input.title,
      periodStart: input.periodStart ?? now - 7 * 24 * 60 * 60 * 1000,
      periodEnd: input.periodEnd ?? now,
      status: "draft",
      summary: input.summary || "",
      highlights: input.highlights || [],
      blockers: input.blockers || [],
      nextSteps: input.nextSteps || [],
      metrics: input.metrics || [],
      issueIds: input.issueIds || [],
      authorId: get().currentUserId,
      source: "manual",
      externalKey: null,
      sourceRefs: [],
      createdAt: now,
      updatedAt: now,
    };
    set((s) => ({ progressCards: [card, ...s.progressCards] }));
    const sb = createClient();
    void (async () => {
      const { data } = await sb
        .from("progress_cards")
        .insert({
          workspace_id: wsId,
          project_id: card.projectId,
          team_id: card.teamId,
          title: card.title,
          period_start: new Date(card.periodStart).toISOString(),
          period_end: new Date(card.periodEnd).toISOString(),
          summary: card.summary,
          highlights: card.highlights,
          blockers: card.blockers,
          next_steps: card.nextSteps,
          metrics: card.metrics as unknown as Database["public"]["Tables"]["progress_cards"]["Insert"]["metrics"],
          issue_ids: card.issueIds,
          author_id: card.authorId,
          source: card.source,
          external_key: card.externalKey,
          source_refs: card.sourceRefs as unknown as Database["public"]["Tables"]["progress_cards"]["Insert"]["source_refs"],
        })
        .select()
        .single();
      if (data) {
        const real = mapProgressCard(data);
        set((s) => ({
          progressCards: s.progressCards.map((c) =>
            c.id === localId ? real : c
          ),
        }));
      }
    })();
    return card;
  },

  updateProgressCard: (id, patch) => {
    set((s) => ({
      progressCards: s.progressCards.map((c) =>
        c.id === id ? { ...c, ...patch, updatedAt: Date.now() } : c
      ),
    }));
    const sb = createClient();
    void (async () => {
      const dbPatch: Database["public"]["Tables"]["progress_cards"]["Update"] = {};
      if (patch.title !== undefined) dbPatch.title = patch.title;
      if (patch.status !== undefined) dbPatch.status = patch.status;
      if (patch.summary !== undefined) dbPatch.summary = patch.summary;
      if (patch.highlights !== undefined) dbPatch.highlights = patch.highlights;
      if (patch.blockers !== undefined) dbPatch.blockers = patch.blockers;
      if (patch.nextSteps !== undefined) dbPatch.next_steps = patch.nextSteps;
      if (patch.metrics !== undefined)
        dbPatch.metrics =
          patch.metrics as unknown as Database["public"]["Tables"]["progress_cards"]["Update"]["metrics"];
      if (patch.issueIds !== undefined) dbPatch.issue_ids = patch.issueIds;
      if (patch.projectId !== undefined) dbPatch.project_id = patch.projectId;
      if (patch.source !== undefined) dbPatch.source = patch.source;
      if (patch.externalKey !== undefined) dbPatch.external_key = patch.externalKey;
      if (patch.sourceRefs !== undefined)
        dbPatch.source_refs =
          patch.sourceRefs as unknown as Database["public"]["Tables"]["progress_cards"]["Update"]["source_refs"];
      if (patch.periodStart !== undefined)
        dbPatch.period_start = new Date(patch.periodStart).toISOString();
      if (patch.periodEnd !== undefined)
        dbPatch.period_end = new Date(patch.periodEnd).toISOString();
      if (Object.keys(dbPatch).length) {
        await sb.from("progress_cards").update(dbPatch).eq("id", id);
      }
    })();
  },

  deleteProgressCard: (id) => {
    set((s) => ({
      progressCards: s.progressCards.filter((c) => c.id !== id),
      selectedProgressCardId:
        s.selectedProgressCardId === id ? null : s.selectedProgressCardId,
    }));
    const sb = createClient();
    sb
      .from("progress_cards")
      .update({ deleted_at: new Date().toISOString(), updated_by: get().currentUserId })
      .eq("id", id)
      .then(({ error }) => {
        if (error) console.error("[deleteProgressCard] failed:", error);
      });
  },

  createNote: (input) => {
    const wsId = get().activeWorkspaceId;
    const localId = uid("note");
    const now = Date.now();
    const note: Note = {
      id: localId,
      workspaceId: wsId,
      projectId: input.projectId ?? null,
      teamId: input.teamId ?? null,
      authorId: get().currentUserId,
      body: input.body,
      tags: input.tags ?? [],
      pinned: input.pinned ?? false,
      createdAt: now,
      updatedAt: now,
    };
    set((s) => ({ notes: [note, ...s.notes] }));
    const sb = createClient();
    void (async () => {
      const { data } = await sb
        .from("notes")
        .insert({
          workspace_id: wsId,
          project_id: input.projectId ?? null,
          team_id: input.teamId ?? null,
          body: input.body,
          tags: input.tags ?? [],
          pinned: input.pinned ?? false,
          author_id: get().currentUserId,
        })
        .select()
        .single();
      if (data) {
        const real = mapNote(data);
        set((s) => ({ notes: s.notes.map((n) => (n.id === localId ? real : n)) }));
      }
    })();
    return note;
  },

  updateNote: (id, patch) => {
    set((s) => ({
      notes: s.notes.map((n) =>
        n.id === id ? { ...n, ...patch, updatedAt: Date.now() } : n
      ),
    }));
    const sb = createClient();
    void (async () => {
      const dbPatch: Database["public"]["Tables"]["notes"]["Update"] = {};
      if (patch.body !== undefined) dbPatch.body = patch.body;
      if (patch.tags !== undefined) dbPatch.tags = patch.tags;
      if (patch.pinned !== undefined) dbPatch.pinned = patch.pinned;
      if (patch.projectId !== undefined) dbPatch.project_id = patch.projectId;
      if (patch.teamId !== undefined) dbPatch.team_id = patch.teamId;
      if (Object.keys(dbPatch).length) {
        await sb.from("notes").update(dbPatch).eq("id", id);
      }
    })();
  },

  deleteNote: (id) => {
    const snapshot = get().notes.find((n) => n.id === id) ?? null;
    set((s) => ({
      notes: s.notes.filter((n) => n.id !== id),
      selectedNoteId: s.selectedNoteId === id ? null : s.selectedNoteId,
    }));
    const sb = createClient();
    sb
      .from("notes")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id)
      .then(({ error }) => {
        if (!error) return;
        console.error("[deleteNote] failed, restoring:", error);
        if (snapshot) set((s) => ({ notes: [...s.notes, snapshot] }));
      });
  },

  // ─────── Feedback (cross-workspace Triage) ───────
  selectFeedback: (id) => set({ selectedFeedbackId: id }),

  submitFeedback: async (input) => {
    const sb = createClient();
    const { data: { user } } = await sb.auth.getUser();
    if (!user) return null;
    const wsId = get().activeWorkspaceId;
    const { data, error } = await sb
      .from("feedback")
      .insert({
        source_workspace_id: wsId,
        target_workspace_id: input.targetWorkspaceId,
        title: input.title,
        body: input.body ?? "",
        severity: input.severity ?? "medium",
        tags: input.tags ?? [],
        source_doc_id: input.sourceDocId ?? null,
        submitter_id: user.id,
      })
      .select(
        "id,source_workspace_id,target_workspace_id,title,body,severity,status,tags,source_doc_id,submitter_id,linked_issue_id,triaged_by,triaged_at,resolved_at,created_at,updated_at"
      )
      .single();
    if (error || !data) {
      set({ error: error?.message ?? "submit_failed" });
      return null;
    }
    const item = mapFeedback(data);
    set((s) => ({ feedback: [item, ...s.feedback] }));
    return item;
  },

  triageFeedback: async (id, action) => {
    // Goes through the MCP API path to keep all triage business logic
    // (issue creation, identifier bumping, dual-side activity) in one
    // place server-side.
    const res = await fetch(`/api/workspaces/${get().activeWorkspaceId}/triage-feedback`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ id, ...action }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      set({ error: err.error ?? "triage_failed" });
      return;
    }
    // Refetch the affected feedback row + any new issue
    const sb = createClient();
    const { data: fb } = await sb
      .from("feedback")
      .select(
        "id,source_workspace_id,target_workspace_id,title,body,severity,status,tags,source_doc_id,submitter_id,linked_issue_id,triaged_by,triaged_at,resolved_at,created_at,updated_at"
      )
      .eq("id", id)
      .maybeSingle();
    if (fb) {
      const updated = mapFeedback(fb);
      set((s) => ({
        feedback: s.feedback.map((f) => (f.id === id ? updated : f)),
      }));
    }
    if (action.action === "convert_to_issue") {
      // Reload issues to pick up the newly created one
      void get().init();
    }
  },

  deleteFeedback: async (id) => {
    const sb = createClient();
    const { error } = await sb.from("feedback").delete().eq("id", id);
    if (error) {
      set({ error: error.message });
      return;
    }
    set((s) => ({
      feedback: s.feedback.filter((f) => f.id !== id),
      selectedFeedbackId: s.selectedFeedbackId === id ? null : s.selectedFeedbackId,
    }));
  },

  linkWorkspaces: async (workspaceId, targetWorkspaceId, direction = "outbound") => {
    const res = await fetch(`/api/workspaces/${workspaceId}/links`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ target_workspace_id: targetWorkspaceId, direction }),
    });
    const data = await res.json();
    if (!data.ok) {
      set({ error: data.error ?? "link_failed" });
      return false;
    }
    // Refetch the links list
    const sb = createClient();
    const { data: rows } = await sb.from("workspace_links").select("*");
    set({ workspaceLinks: (rows ?? []).map(mapWorkspaceLink) });
    return true;
  },

  unlinkWorkspaces: async (workspaceId, otherId, direction) => {
    const res = await fetch(
      `/api/workspaces/${workspaceId}/links?other_id=${otherId}&direction=${direction}`,
      { method: "DELETE" }
    );
    const data = await res.json();
    if (!data.ok) {
      set({ error: data.error ?? "unlink_failed" });
      return false;
    }
    set((s) => {
      const source = direction === "outbound" ? workspaceId : otherId;
      const target = direction === "outbound" ? otherId : workspaceId;
      return {
        workspaceLinks: s.workspaceLinks.filter(
          (l) =>
            !(l.sourceWorkspaceId === source && l.targetWorkspaceId === target)
        ),
      };
    });
    return true;
  },
}));

export function getUserById(id: string | null | undefined) {
  if (!id) return null;
  return useStore.getState().users.find((u) => u.id === id) || null;
}
